import React, { useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import styled from "styled-components";
import { FaUsers, FaCheckCircle, FaEnvelope, FaPhone, FaPlay, FaHandshake, FaTimes, FaArrowUp, FaArrowDown, FaArrowRight, FaMousePointer } from "react-icons/fa";
import SummaryCard from './SummaryCard';
import FlipCard from './FlipCard';
import SummarySkeleton from './SummarySkeleton';
import { useLeadsContext } from '../../../context/LeadsContext';

// Styled components for gap between cards with percentage
const ConversionGap = styled.div`
  position: relative;
  width: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ConversionPercentage = styled.div`
  position: absolute;
  background: #1890ff;
  color: white;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 6px;
  border-radius: 12px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2);
  z-index: 10;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

// Helper function to format trend indicators
const formatTrendIcon = (trend) => {
  switch (trend) {
    case 'up': return <FaArrowUp style={{ color: '#28a745', fontSize: '10px', transform: 'rotate(45deg)' }} />;
    case 'down': return <FaArrowDown style={{ color: '#dc3545', fontSize: '10px', transform: 'rotate(-45deg)' }} />;
    default: return <FaArrowRight style={{ color: '#6c757d', fontSize: '10px' }} />;
  }
};

// Helper function to format percentage change
const formatPercentageChange = (change, trend) => {
  if (!change && change !== 0) return '';
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
};

export const LeadSummary = ({ data = {}, onSummaryCardClick, isLoading }) => {
  // Get funnel data and other context from LeadsContext
  const { funnelData, summaryData, columns, loadSummaryData, updatedSummaryCards } = useLeadsContext();
  
  const selectedDate = useSelector((state) => state.leads && state.leads.selectedDate || moment().format("MM-YYYY"));

  // Legacy icon mapping (moved up for use in callback)
  const getColumnIcon = useCallback((columnId) => {
    switch (columnId) {
      case 1: return <FaPlay />;
      case 2: return <FaPhone />;
      case 3: return <FaUsers />;
      case 4: return <FaEnvelope />;
      case 5: return <FaCheckCircle />;
      default: return <FaUsers />;
    }
  }, []);


  // Process funnel data to create summary cards
  const summaryItems = useMemo(() => {
    if (!funnelData || !funnelData.current_period) {
      // Display empty/0 values when no funnel data is available, but filter out Oportunidades with 0
      const emptyCards = [
        { title: 'Cliques', icon: <FaMousePointer />, done: 0, undone: 0, percentage: '', color: '#6f42c1', tooltip: 'Total de cliques rastreados', stageKey: 'cliques', columnId: 'funnel_cliques' },
        { title: 'Leads', icon: <FaUsers />, done: 0, undone: 0, percentage: '', color: '#007bff', tooltip: 'Total de leads criados', stageKey: 'leads', columnId: 1 },
        { title: 'Oportunidades', icon: <FaHandshake />, done: 0, undone: 0, percentage: '', color: '#17a2b8', tooltip: 'Leads qualificados como oportunidades', stageKey: 'oportunidades', columnId: 2 },
        { title: 'Propostas', icon: <FaEnvelope />, done: 0, undone: 0, percentage: '', color: '#ffc107', tooltip: 'Propostas enviadas', stageKey: 'propostas', columnId: 'funnel_propostas' },
        { title: 'Positivo', icon: <FaCheckCircle />, done: 0, undone: 0, percentage: '', color: '#28a745', tooltip: 'Negócios fechados com sucesso', stageKey: 'positivo', columnId: 3 },
        { title: 'Negativo', icon: <FaTimes />, done: 0, undone: 0, percentage: '', color: '#dc3545', tooltip: 'Negócios perdidos', stageKey: 'negativo', columnId: 4 }
      ];
      
      // Filter out Oportunidades card if it has 0 value
      return emptyCards.filter(card => 
        card.stageKey !== 'oportunidades' || card.done > 0
      );
    }

    // Handle actual funnel structure with current_period and changes
    const { current_period, changes } = funnelData;
    // Check if metrics are nested or directly in current_period
    const metrics = (current_period && current_period.metrics) || current_period || {};
    
    // Create funnel-based summary cards using the correct API structure
    const funnelCards = [
      {
        title: 'Cliques',
        icon: <FaMousePointer />,
        done: metrics.cliques || 0,
        undone: 0,
        percentage: '', // Percentage shown in gap, not on card
        color: '#6f42c1',
        tooltip: `Total de cliques rastreados`,
        stageKey: 'cliques',
        columnId: 'funnel_cliques',
        revenue: null, // No revenue for clicks
        trendIcon: (changes && changes.cliques) ? formatTrendIcon(changes.cliques.direction) : null,
        trendText: (changes && changes.cliques) ? formatPercentageChange(changes.cliques.percentage, changes.cliques.direction) : ''
      },
      {
        title: 'Leads',
        icon: <FaUsers />,
        done: metrics.leads || 0,
        undone: 0,
        percentage: '', // Percentage shown in gap, not on card
        color: '#007bff',
        tooltip: `Total de leads criados`,
        stageKey: 'leads',
        columnId: 1, // Map to actual column for click handling
        revenue: (summaryData && summaryData.revenue_by_stage && summaryData.revenue_by_stage['1']) || null,
        trendIcon: (changes && changes.leads) ? formatTrendIcon(changes.leads.direction) : null,
        trendText: (changes && changes.leads) ? formatPercentageChange(changes.leads.percentage, changes.leads.direction) : ''
      },
      {
        title: 'Oportunidades',
        icon: <FaHandshake />,
        done: metrics.oportunidades || 0,
        undone: 0,
        percentage: '', // Percentage shown in gap, not on card
        color: '#17a2b8',
        tooltip: `Leads qualificados como oportunidades`,
        stageKey: 'oportunidades',
        columnId: 2, // Map to actual column for click handling
        revenue: (summaryData && summaryData.revenue_by_stage && summaryData.revenue_by_stage['2']) || null,
        trendIcon: (changes && changes.oportunidades) ? formatTrendIcon(changes.oportunidades.direction) : null,
        trendText: (changes && changes.oportunidades) ? formatPercentageChange(changes.oportunidades.percentage, changes.oportunidades.direction) : ''
      },
      {
        title: 'Propostas',
        icon: <FaEnvelope />,
        done: metrics.propostas || 0,
        undone: 0,
        percentage: '', // Percentage shown in gap, not on card
        color: '#ffc107',
        tooltip: `Propostas enviadas`,
        stageKey: 'propostas',
        columnId: 'funnel_propostas',
        revenue: null, // Proposals don't have direct revenue mapping
        trendIcon: (changes && changes.propostas) ? formatTrendIcon(changes.propostas.direction) : null,
        trendText: (changes && changes.propostas) ? formatPercentageChange(changes.propostas.percentage, changes.propostas.direction) : ''
      },
      {
        title: 'Positivo',
        icon: <FaCheckCircle />,
        done: metrics.positivo || 0,
        undone: 0,
        percentage: '', // No percentage after final positive outcomes
        color: '#28a745',
        tooltip: `Negócios fechados com sucesso`,
        stageKey: 'positivo',
        columnId: 3, // Map to actual column for click handling
        revenue: (summaryData && summaryData.revenue_by_stage && summaryData.revenue_by_stage['3']) || null,
        trendIcon: (changes && changes.positivo) ? formatTrendIcon(changes.positivo.direction) : null,
        trendText: (changes && changes.positivo) ? formatPercentageChange(changes.positivo.percentage, changes.positivo.direction) : ''
      },
      {
        title: 'Negativo',
        icon: <FaTimes />,
        done: metrics.negativo || 0,
        undone: 0,
        percentage: '', // No percentage after final negative outcomes
        color: '#dc3545',
        tooltip: `Negócios perdidos`,
        stageKey: 'negativo',
        columnId: 4, // Map to actual column for click handling
        revenue: (summaryData && summaryData.revenue_by_stage && summaryData.revenue_by_stage['4']) || null,
        trendIcon: (changes && changes.negativo) ? formatTrendIcon(changes.negativo.direction) : null,
        trendText: (changes && changes.negativo) ? formatPercentageChange(changes.negativo.percentage, changes.negativo.direction) : ''
      }
    ];

    // Filter out Oportunidades card if it has 0 value
    return funnelCards.filter(card => 
      card.stageKey !== 'oportunidades' || card.done > 0
    );
  }, [funnelData, summaryData]);

  const handleSummaryCardClick = useCallback((item) => {
    if (onSummaryCardClick) {
      // For funnel cards, we need to find relevant column data
      let columnId = item.columnId;
      let columnLeads = [];

      if (typeof columnId === 'number') {
        // Direct column mapping
        const column = columns.find(col => col.id === columnId);
        columnLeads = column ? column.leads || [] : [];
      } else if (columnId === 'total') {
        // Total leads - combine all columns
        columnLeads = columns.reduce((acc, col) => [...acc, ...(col.leads || [])], []);
      }
      // For funnel-specific metrics (cliques, leads, etc.), we might not have direct column mapping
      // but we can still provide the metric data

      onSummaryCardClick({
        stage: item.stageKey,
        title: item.title,
        columnId: item.columnId,
        leads: columnLeads,
        count: item.done,
        isFunnelMetric: typeof item.columnId === 'string' && item.columnId.startsWith('funnel_')
      });
    }
  }, [columns, onSummaryCardClick]);

  // Show skeleton during initial loading
  if (isLoading && (!data || Object.keys(data).length === 0)) {
    return <SummarySkeleton />;
  }

  
  return (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      padding: '0 20px',
      width: '100%'
    }}>
      {summaryItems.map((item, index) => {
        const isLastCard = index === summaryItems.length - 1;
        const nextItem = !isLastCard ? summaryItems[index + 1] : null;
        
        // Get conversion percentage that should appear in the gap AFTER this card
        let conversionPercentage = '';
        if (!isLastCard && funnelData && funnelData.current_period) {
          // Use the same metrics object we determined above
          const currentMetrics = (funnelData.current_period.metrics) || funnelData.current_period || {};
          
          // Calculate conversion rates between funnel stages
          const calculateConversion = (current, next) => {
            if (!current || !next) return '';
            const rate = (next / current) * 100;
            return rate > 0 ? `${rate.toFixed(1)}%` : '';
          };
          
          // Map each card to its outgoing conversion rate
          switch (item.stageKey) {
            case 'cliques':
              conversionPercentage = calculateConversion(currentMetrics.cliques, currentMetrics.leads);
              break;
            case 'leads':
              conversionPercentage = calculateConversion(currentMetrics.leads, currentMetrics.oportunidades);
              break;
            case 'oportunidades':
              conversionPercentage = calculateConversion(currentMetrics.oportunidades, currentMetrics.propostas);
              break;
            case 'propostas':
              conversionPercentage = calculateConversion(currentMetrics.propostas, currentMetrics.positivo);
              break;
            default:
              conversionPercentage = '';
          }
        }
        
        return (
          <React.Fragment key={item.stageKey || index}>
            <div style={{ flex: 1 }}>
              {item.revenue ? (
                <FlipCard
                  item={item}
                  revenue={item.revenue}
                  onClick={() => handleSummaryCardClick(item)}
                  showTrend={!!funnelData}
                  isHighlighted={updatedSummaryCards.has(item.stageKey)}
                />
              ) : (
                <SummaryCard
                  item={item}
                  onClick={handleSummaryCardClick}
                  showTrend={!!funnelData}
                  isHighlighted={updatedSummaryCards.has(item.stageKey)}
                  revenue={item.revenue}
                />
              )}
            </div>
            {!isLastCard && (
              <ConversionGap>
                {conversionPercentage && (
                  <ConversionPercentage>
                    {conversionPercentage}
                  </ConversionPercentage>
                )}
              </ConversionGap>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};